import React, { useEffect, useState } from 'react';
import { useNotify } from '../contexts/NotificationContext';

// SVG Icons
const SuccessIcon = () => (
  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const ErrorIcon = () => (
  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const WarningIcon = () => (
  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.272 16.5c-.77.833.192 2.5 1.732 2.5z" />
  </svg>
);

const InfoIcon = () => (
  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const NotificationContainer = () => {
    const { notifications, removeNotification } = useNotify();
    const [isVisible, setIsVisible] = useState({});

    // Get close button color based on type
    const getCloseButtonColor = (type) => {
        switch (type) {
            case 'success':
                return 'text-green-600 hover:bg-green-50';
            case 'error':
                return 'text-red-600 hover:bg-red-50';
            case 'warning':
                return 'text-yellow-600 hover:bg-yellow-50';
            case 'info':
                return 'text-blue-600 hover:bg-blue-50';
            default:
                return 'text-blue-600 hover:bg-blue-50';
        }
    };

    // Get icon color based on type
    const getIconColor = (type) => {
        switch (type) {
            case 'success':
                return 'text-green-600';
            case 'error':
                return 'text-red-600';
            case 'warning':
                return 'text-yellow-600';
            case 'info':
                return 'text-blue-600';
            default:
                return 'text-blue-600';
        }
    };

    // Get icon background color
    const getIconBgColor = (type) => {
        switch (type) {
            case 'success':
                return 'bg-green-100';
            case 'error':
                return 'bg-red-100';
            case 'warning':
                return 'bg-yellow-100';
            case 'info':
                return 'bg-blue-100';
            default:
                return 'bg-blue-100';
        }
    };

    // Get primary button color for actions
    const getPrimaryButtonColor = (type) => {
        switch (type) {
            case 'success':
                return 'bg-green-600 hover:bg-green-700 focus:ring-green-500';
            case 'error':
                return 'bg-red-600 hover:bg-red-700 focus:ring-red-500';
            case 'warning':
                return 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500';
            case 'info':
                return 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500';
            default:
                return 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500';
        }
    };

    const getIcon = (type) => {
        switch (type) {
            case 'success':
                return <SuccessIcon />;
            case 'error':
                return <ErrorIcon />;
            case 'warning':
                return <WarningIcon />;
            case 'info':
                return <InfoIcon />;
            default:
                return <InfoIcon />;
        }
    };

    // Handle close button click
    const handleClose = (e, notification) => {
        e.stopPropagation(); // Prevent event bubbling
        if (notification.onClose) notification.onClose();
        removeNotification(notification.id);
    };

    // Handle backdrop click
    const handleBackdropClick = (e, notification) => {
        e.stopPropagation();
        if (notification.onClose) notification.onClose();
        removeNotification(notification.id);
    };

    // Set visibility for animations
    useEffect(() => {
        notifications.forEach(notification => {
            if (!isVisible[notification.id]) {
                setTimeout(() => {
                    setIsVisible(prev => ({ ...prev, [notification.id]: true }));
                }, 10);
            }
        });
    }, [notifications]);

    if (notifications.length === 0) return null;

    return (
        <>
            {notifications.map(notification => (
                <div 
                    key={notification.id} 
                    className="fixed inset-0 z-50 overflow-y-auto"
                >
                    {/* Backdrop with fade animation */}
                    <div 
                        className={`fixed inset-0 bg-black transition-opacity duration-300 ${
                            isVisible[notification.id] ? 'bg-opacity-50' : 'bg-opacity-0'
                        }`}
                        onClick={(e) => handleBackdropClick(e, notification)}
                    />
                    
                    {/* Modal Container */}
                    <div className="flex items-center justify-center min-h-screen px-4 py-4 text-center">
                        {/* Modal Content with scale animation */}
                        <div 
                            className={`relative inline-block align-middle bg-white rounded-lg shadow-xl w-full max-w-md transform transition-all duration-300 ${
                                isVisible[notification.id] 
                                    ? 'opacity-100 translate-y-0 scale-100' 
                                    : 'opacity-0 translate-y-4 scale-95'
                            }`}
                            onClick={(e) => e.stopPropagation()} // Prevent backdrop click when clicking modal
                        >
                            {/* Header with title and colored close button */}
                            <div className="flex items-center justify-between px-6 py-4 border-b">
                                <div className="flex items-center space-x-3">
                                    {/* Icon in header */}
                                    {notification.showIcon !== false && (
                                        <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${getIconBgColor(notification.type)}`}>
                                            <span className={getIconColor(notification.type)}>
                                                {getIcon(notification.type)}
                                            </span>
                                        </div>
                                    )}
                                    
                                    <h3 className="text-lg font-semibold text-gray-900">
                                        {notification.title}
                                    </h3>
                                </div>
                                
                                {/* Simple × Close Button with colored text */}
                                {notification.showClose !== false && (
                                    <button
                                        onClick={(e) => handleClose(e, notification)}
                                        className={`text-xl font-bold p-2 rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300 ${getCloseButtonColor(notification.type)}`}
                                        aria-label="Close"
                                    >
                                        ×
                                    </button>
                                )}
                            </div>
                            
                            {/* Body */}
                            <div className="px-6 py-5">
                                {/* Message */}
                                <div className="text-left">
                                    <p className="text-gray-700">
                                        {notification.message}
                                    </p>
                                    
                                    {notification.component && (
                                        <div className="mt-4">
                                            <notification.component />
                                        </div>
                                    )}
                                </div>
                                
                                {/* Actions */}
                                {notification.actions && notification.actions.length > 0 && (
                                    <div className="mt-6 flex justify-center space-x-3">
                                        {notification.actions.map((action, index) => (
                                            <button
                                                key={index}
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (action.onClick) action.onClick();
                                                    if (notification.onActionClick) notification.onActionClick(action);
                                                }}
                                                className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                                                    action.variant === 'outline'
                                                        ? 'border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:ring-gray-500'
                                                        : `${getPrimaryButtonColor(notification.type)} text-white`
                                                }`}
                                            >
                                                {action.text}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                
                                {/* Simple OK Button for basic notifications */}
                                {(!notification.actions || notification.actions.length === 0) && (
                                    <div className="mt-6 flex justify-center">
                                        <button
                                            type="button"
                                            onClick={(e) => handleClose(e, notification)}
                                            className={`px-6 py-2 text-sm font-medium rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${getPrimaryButtonColor(notification.type)} text-white`}
                                        >
                                            OK
                                        </button>
                                    </div>
                                )}
                            </div>
                            
                            {/* Auto-close Progress Bar */}
                            {notification.autoClose !== false && notification.duration > 0 && (
                                <div className="h-1 bg-gray-200 rounded-b-lg overflow-hidden">
                                    <div 
                                        className={`h-full ${
                                            notification.type === 'success' ? 'bg-green-500' :
                                            notification.type === 'error' ? 'bg-red-500' :
                                            notification.type === 'warning' ? 'bg-yellow-500' :
                                            'bg-blue-500'
                                        }`}
                                        style={{ 
                                            width: '100%',
                                            animation: `shrink ${notification.duration}ms linear forwards`
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            ))}
            
            {/* Inline CSS for animations */}
            <style jsx>{`
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }
            `}</style>
        </>
    );
};

export default NotificationContainer;